import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../../foundation/altzero_base/base/components/ui/input";
import {
  Edit3,
  Eye,
  Save,
  Loader2,
  Plus,
  Trash2,
  MoreHorizontal,
  GripVertical,
  Check,
  X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import YooptaMarkdownEditor from "./YooptaMarkdownEditor";
import PerfectMarkdownRenderer from "../common/PerfectMarkdownRenderer";
import { parseMarkdownForTemplateEditor, sectionsToMarkdown, TemplateSection, SectionMetadata } from "../../utils/sectionUtils";
// 🚨 NEW: Import enhanced editor components
import { SortableSectionList } from "./shared/SortableSectionList";
import { EditorLayout, EditorActionButtons } from "./shared/EditorLayout";

interface YooptaTemplateEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSave?: () => void;
  readonly?: boolean;
  userId?: string;
  className?: string;
  isSaving?: boolean;
}

// 🚨 CONSISTENT H2 STANDARD: Use centralized section parsing
const extractSectionsFromMarkdown = (content: string): TemplateSection[] => {
  // Use the centralized parsing function for consistency
  const parsedSections = parseMarkdownForTemplateEditor(content);

  // If no H2 sections found, create a default section for new templates
  if (parsedSections.length === 0 && content.trim()) {
    console.log('🔧 No H2 sections found, creating default section for template editor');
    return [{
      id: "section-1",
      title: "Introduction",
      content: content.trim(),
      edited: false,
    }];
  }

  console.log("📋 Extracted template sections using H2 standard:", parsedSections);
  return parsedSections;
};

// Use the imported sectionsToMarkdown function for proper H2 formatting

export default function YooptaTemplateEditor({
  content,
  onChange,
  onSave,
  readonly = false,
  userId,
  className = "",
  isSaving = false,
}: YooptaTemplateEditorProps) {
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<{ [sectionId: string]: string }>({});
  const [editorKey, setEditorKey] = useState(0);
  const [previewUpdateTrigger, setPreviewUpdateTrigger] = useState(0);

  // 🚨 NEW: Enhanced editor state for two-tab layout (removed document preview)
  const [activeTab, setActiveTab] = useState<'edit' | 'section-preview'>('edit');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 🚨 NEW: Separate state for section preview selection
  const [selectedSectionForPreview, setSelectedSectionForPreview] = useState<string | null>(null);

  // Enhanced section management state
  const [editingTitles, setEditingTitles] = useState<Set<string>>(new Set());
  const [titleInputs, setTitleInputs] = useState<{ [sectionId: string]: string }>({});
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);

  // Metadata management state
  const [sectionMetadata, setSectionMetadata] = useState<{ [sectionId: string]: SectionMetadata }>({});

  // Extract sections from content
  useEffect(() => {
    console.log('🔄 YooptaTemplateEditor: Processing content:', {
      contentLength: content?.length || 0,
      hasContent: !!content,
      preview: content ? content.substring(0, 100) : 'no content'
    });

    let extractedSections = extractSectionsFromMarkdown(content);

    // 🚨 FIX: If no content or no sections, create a default section for new templates
    if (!content || content.trim() === '' || extractedSections.length === 0) {
      console.log('🔧 Creating default section for new template');
      extractedSections = [{
        id: 'section-1',
        title: 'Introduction',
        content: 'Start writing your template content here...',
        edited: false
      }];
    }

    setSections(extractedSections);

    // CRITICAL FIX: Initialize editedContent with all section content
    const newEditedContent: { [sectionId: string]: string } = {};
    const newMetadata: { [sectionId: string]: SectionMetadata } = {};
    extractedSections.forEach(section => {
      newEditedContent[section.id] = section.content || "";
      newMetadata[section.id] = section.metadata || { type: 'static' };
    });

    setEditedContent(prev => {
      // Merge with existing editedContent to preserve any unsaved changes
      const merged = { ...prev, ...newEditedContent };
      console.log("📝 Initialized editedContent for all sections:", {
        sectionCount: extractedSections.length,
        editedContentKeys: Object.keys(merged),
        contentLengths: Object.entries(merged).map(([id, content]) => ({
          id,
          length: content?.length || 0
        }))
      });
      return merged;
    });

    // Initialize metadata for all sections
    setSectionMetadata(prev => {
      const merged = { ...prev, ...newMetadata };
      console.log("📋 Initialized metadata for all sections:", merged);
      return merged;
    });

    // 🚨 ENHANCED: Only auto-select if selected section doesn't exist in current sections
    if (extractedSections.length > 0) {
      // If selected section doesn't exist in current sections, clear selection
      if (selectedSectionId && !extractedSections.find(s => s.id === selectedSectionId)) {
        setSelectedSectionId(null);
        setActiveTab('edit');
        setIsEditing(false);
        console.log('🔧 Selected section not found, cleared selection');
      }
    }
  }, [content, selectedSectionId]);

  // 🚨 REMOVED: No longer auto-select first section on mount
  // Users can manually select sections as needed

  // Get currently selected section
  const selectedSection = sections.find(s => s.id === selectedSectionId);

  // Handle section content change
  const handleSectionContentChange = useCallback((sectionId: string, newContent: string) => {
    console.log("🔄 Template section content changed:", {
      sectionId,
      contentLength: newContent?.length,
      preview: newContent ? newContent.substring(0, 50) + '...' : 'empty'
    });

    // Update edited content tracking FIRST
    setEditedContent(prev => {
      const updated = {
        ...prev,
        [sectionId]: newContent,
      };
      console.log("📝 Updated editedContent:", Object.keys(updated));
      return updated;
    });

    // Update sections immediately for preview
    setSections(prev => {
      const updated = prev.map(section =>
        section.id === sectionId
          ? { ...section, content: newContent, edited: true }
          : section
      );

      // Combine all sections and call parent onChange
      const combinedContent = sectionsToMarkdown(updated);
      onChange(combinedContent);

      return updated;
    });

    // Trigger preview update
    setPreviewUpdateTrigger(prev => prev + 1);
    setHasUnsavedChanges(true);
  }, [onChange]);

  // 🚨 ENHANCED: Context-aware section selection based on active tab
  const handleSectionSelect = (sectionId: string) => {
    const targetSection = sections.find(s => s.id === sectionId);

    console.log("🎯 Section selected:", {
      sectionId,
      activeTab,
      previousSectionId: selectedSectionId,
      targetSectionContent: targetSection?.content?.substring(0, 50) + '...',
      targetSectionContentLength: targetSection?.content?.length || 0,
      hasEditedContent: editedContent[sectionId] !== undefined,
      editedContentKeys: Object.keys(editedContent),
      editedContentForSection: editedContent[sectionId]?.substring(0, 50) + '...'
    });

    if (activeTab === 'edit') {
      // Edit tab behavior: Open editor with section content
      // CRITICAL FIX: Always initialize editedContent with the section's current content
      // This ensures the editor loads the latest saved content, not just unsaved changes
      if (targetSection) {
        setEditedContent(prev => ({
          ...prev,
          [sectionId]: targetSection.content || ""
        }));

        console.log("🔄 Initialized editedContent for section:", {
          sectionId,
          contentLength: targetSection.content?.length || 0,
          contentPreview: targetSection.content?.substring(0, 100) + '...'
        });
      }

      setSelectedSectionId(sectionId);
      setIsEditing(true);
      // Force editor re-render to pick up the new content
      setEditorKey(prev => prev + 1);
    } else if (activeTab === 'section-preview') {
      // Section Preview tab behavior: Update preview to show selected section
      setSelectedSectionForPreview(sectionId);
      console.log("🔄 Updated section preview selection:", sectionId);
      // Don't open editor, just update preview
    }
  };

  const handleSectionReorder = (reorderedSections: TemplateSection[]) => {
    setSections(reorderedSections);
    setHasUnsavedChanges(true);
    const newMarkdown = sectionsToMarkdown(reorderedSections);
    onChange(newMarkdown);
  };

  // Handle metadata changes
  const handleMetadataChange = useCallback((sectionId: string, metadata: SectionMetadata) => {
    console.log('📋 Updating metadata for section:', sectionId, metadata);

    setSectionMetadata(prev => ({
      ...prev,
      [sectionId]: metadata
    }));

    // Update the section in sections array
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, metadata }
        : section
    ));

    setHasUnsavedChanges(true);

    // Update the markdown content with new metadata
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, metadata }
        : section
    );
    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);
  }, [sections, onChange]);

  const handleSectionTitleChange = (sectionId: string, newTitle: string) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, title: newTitle, edited: true }
        : section
    ));
    setHasUnsavedChanges(true);
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, title: newTitle, edited: true }
        : section
    );
    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);
  };

  const handleSectionDelete = (sectionId: string) => {
    if (sections.length <= 1) {
      return; // Don't delete the last section
    }

    const section = sections.find(s => s.id === sectionId);
    if (confirm(`Are you sure you want to delete "${section?.title}"?`)) {
      setSections(prev => prev.filter(s => s.id !== sectionId));

      // If deleted section was selected, select the first remaining section
      if (selectedSectionId === sectionId) {
        const remainingSections = sections.filter(s => s.id !== sectionId);
        setSelectedSectionId(remainingSections.length > 0 ? remainingSections[0].id : null);
      }

      setHasUnsavedChanges(true);
      const updatedSections = sections.filter(s => s.id !== sectionId);
      const newMarkdown = sectionsToMarkdown(updatedSections);
      onChange(newMarkdown);
    }
  };

  const handleSectionAdd = () => {
    console.log("🔄 Adding new section to template editor");

    const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const sectionNumber = sections.length + 1;
    const newSection: TemplateSection = {
      id: newSectionId,
      title: `New Section ${sectionNumber}`,
      content: `This is the content for section ${sectionNumber}. You can edit this content as needed.

Add your template content here with:
- **Bold text**
- *Italic text*
- Lists and bullet points
- [Links](https://example.com)

### Subsection
Add more detailed content here.`,
      edited: false,
    };

    setSections(prev => {
      const updated = [...prev, newSection];
      console.log("📝 Template sections after add:", updated.map(s => ({ id: s.id, title: s.title })));
      console.log("🎯 Total sections now:", updated.length);
      return updated;
    });

    setSelectedSectionId(newSectionId);
    setActiveTab('edit');
    setIsEditing(true);
    setHasUnsavedChanges(true);

    const updatedSections = [...sections, newSection];
    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);

    console.log("✅ New section added and selected:", newSectionId);
  };

  const handleSectionAddAfter = (afterSectionId: string) => {
    const afterIndex = sections.findIndex(s => s.id === afterSectionId);
    const newSectionId = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newSection: TemplateSection = {
      id: newSectionId,
      title: "New Section",
      content: "Start writing your content here...",
      edited: false,
    };

    setSections(prev => {
      const updatedSections = [...prev];
      updatedSections.splice(afterIndex + 1, 0, newSection);
      return updatedSections;
    });

    setSelectedSectionId(newSectionId);
    setActiveTab('edit');
    setIsEditing(true);
    setHasUnsavedChanges(true);

    const updatedSections = [...sections];
    updatedSections.splice(afterIndex + 1, 0, newSection);
    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);
  };

  const handleSectionEdit = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setActiveTab('edit');
    setIsEditing(true);
  };

  // Enhanced section management functions (keeping existing ones)
  const handleAddSection = useCallback(() => {
    handleSectionAdd();
  }, [sections, onChange]);

  const handleDeleteSection = useCallback((sectionId: string) => {
    setSections(prev => prev.filter(section => section.id !== sectionId));

    // Clear selection if deleted section was selected
    if (selectedSectionId === sectionId) {
      setSelectedSectionId(null);
      setIsEditing(false);
    }

    // Clear edited content for deleted section
    setEditedContent(prev => {
      const newContent = { ...prev };
      delete newContent[sectionId];
      return newContent;
    });

    // Update the overall content
    const updatedSections = sections.filter(section => section.id !== sectionId);
    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);
  }, [sections, selectedSectionId, onChange]);

  const handleTitleEdit = useCallback((sectionId: string, newTitle: string) => {
    setSections(prev => prev.map(section =>
      section.id === sectionId
        ? { ...section, title: newTitle.trim(), edited: true }
        : section
    ));

    // Update the overall content
    const updatedSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, title: newTitle.trim(), edited: true }
        : section
    );

    const newMarkdown = sectionsToMarkdown(updatedSections);
    onChange(newMarkdown);
  }, [sections, onChange]);

  const toggleTitleEditing = useCallback((sectionId: string) => {
    const newEditing = new Set(editingTitles);
    if (newEditing.has(sectionId)) {
      newEditing.delete(sectionId);
      // Save the title when exiting edit mode
      const newTitle = titleInputs[sectionId];
      if (newTitle && newTitle.trim()) {
        handleTitleEdit(sectionId, newTitle);
      }
    } else {
      newEditing.add(sectionId);
      // Initialize the input with current title
      const section = sections.find(s => s.id === sectionId);
      if (section) {
        setTitleInputs(prev => ({
          ...prev,
          [sectionId]: section.title
        }));
      }
    }
    setEditingTitles(newEditing);
  }, [editingTitles, titleInputs, sections, handleTitleEdit]);

  // Handle save
  const handleSave = async () => {
    if (!selectedSection || readonly) return;

    try {
      // Get the edited content for this section
      const currentContent = editedContent[selectedSection.id] || selectedSection.content;

      // Update the section
      const updatedSections = sections.map(section =>
        section.id === selectedSection.id
          ? { ...section, content: currentContent, edited: true }
          : section
      );

      setSections(updatedSections);

      // Combine all sections and update parent
      const combinedContent = sectionsToMarkdown(updatedSections);
      onChange(combinedContent);

      // Clear edited content for this section
      setEditedContent(prev => {
        const newEditedContent = { ...prev };
        delete newEditedContent[selectedSection.id];
        return newEditedContent;
      });

      // Call parent save if provided
      if (onSave) {
        await onSave();
      }

      // Switch to preview mode
      setIsEditing(false);
      setEditorKey(prev => prev + 1);

    } catch (error) {
      console.error("Failed to save template section:", error);
    }
  };

  // Handle cancel editing
  const handleCancelEdit = () => {
    if (selectedSection) {
      // Remove any unsaved changes for this section
      setEditedContent(prev => {
        const newEditedContent = { ...prev };
        delete newEditedContent[selectedSection.id];
        return newEditedContent;
      });
    }
    setIsEditing(false);
    setEditorKey(prev => prev + 1);
  };

  if (sections.length === 0) {
    return (
      <div className={`template-editor-empty ${className}`}>
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg font-medium mb-2">No content available</div>
          <div className="text-sm">
            Start typing to create template sections
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`yoopta-template-editor ${className} h-full overflow-hidden`}>
      {/* 🚨 NEW: Enhanced Editor Layout with Three-Tab Interface */}
      <div className="h-full min-h-0 overflow-hidden bg-gray-50 rounded-lg border">
        <EditorLayout
          activeTab={activeTab}
          onTabChange={setActiveTab}
          selectedSectionTitle={sections.find(s => s.id === selectedSectionId)?.title}
          totalSections={sections.length}
          showDocumentPreview={false}
          sectionList={
            <SortableSectionList
              sections={sections}
              selectedSectionId={selectedSectionId}
              selectedSectionForPreview={selectedSectionForPreview}
              activeTab={activeTab}
              onSectionSelect={handleSectionSelect}
              onSectionReorder={handleSectionReorder}
              onSectionTitleChange={handleSectionTitleChange}
              onSectionDelete={handleSectionDelete}
              onSectionAdd={handleSectionAdd}
              onSectionAddAfter={handleSectionAddAfter}
              onSectionEdit={handleSectionEdit}
              onMetadataChange={handleMetadataChange}
              readonly={readonly}
            />
          }
          editorContent={
            selectedSectionId ? (
              <div className="h-full flex flex-col">
                <YooptaMarkdownEditor
                  key={`editor-${editorKey}-${selectedSectionId}`}
                  markdownContent={(() => {
                    // Get the current section
                    const currentSection = sections.find(s => s.id === selectedSectionId);

                    // CRITICAL FIX: Always use the section's current content as the source of truth
                    // The editedContent should be initialized with section content when section is selected
                    let content = "";

                    // If we have editedContent for this section, use it (this includes both unsaved changes and initialized content)
                    if (editedContent[selectedSectionId] !== undefined) {
                      content = editedContent[selectedSectionId];
                    }
                    // Otherwise, use the section's content directly and initialize editedContent
                    else if (currentSection?.content) {
                      content = currentSection.content;
                      // Initialize editedContent with section content
                      setEditedContent(prev => ({
                        ...prev,
                        [selectedSectionId]: currentSection.content || ""
                      }));
                    }
                    // Final fallback
                    else {
                      content = "";
                    }

                    console.log("🔍 Editor content loading:", {
                      sectionId: selectedSectionId,
                      hasEditedContent: editedContent[selectedSectionId] !== undefined,
                      editedContentLength: editedContent[selectedSectionId]?.length || 0,
                      sectionContentLength: currentSection?.content?.length || 0,
                      finalContentLength: content.length,
                      finalContentPreview: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
                      contentSource: editedContent[selectedSectionId] !== undefined ? 'editedContent' : 'sectionContent'
                    });

                    return content;
                  })()}
                  onChange={(newContent) => {
                    console.log("🔄 Editor content changed:", {
                      sectionId: selectedSectionId,
                      newContentLength: newContent?.length || 0,
                      preview: newContent ? newContent.substring(0, 50) + '...' : 'empty'
                    });

                    // Update edited content tracking immediately
                    setEditedContent(prev => ({
                      ...prev,
                      [selectedSectionId]: newContent
                    }));

                    // Update the section content in sections state
                    setSections(prev => prev.map(section =>
                      section.id === selectedSectionId
                        ? { ...section, content: newContent, edited: true }
                        : section
                    ));

                    // Update overall content for parent component
                    const updatedSections = sections.map(section =>
                      section.id === selectedSectionId
                        ? { ...section, content: newContent, edited: true }
                        : section
                    );
                    const newMarkdown = sectionsToMarkdown(updatedSections);
                    onChange(newMarkdown);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder={`Edit ${sections.find(s => s.id === selectedSectionId)?.title} content...`}
                  className="h-full w-full"
                  autoFocus={true}
                  userId={userId}
                  bucketName="templateimages"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Edit3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a section to start editing</p>
                </div>
              </div>
            )
          }
          previewContent={
            (() => {
              // 🚨 ENHANCED: Context-aware preview content based on active tab
              const sectionIdToPreview = activeTab === 'section-preview'
                ? selectedSectionForPreview
                : selectedSectionId;

              if (sectionIdToPreview) {
                const selectedSection = sections.find(s => s.id === sectionIdToPreview);
                if (!selectedSection) {
                  return (
                    <div className="p-6">
                      <p className="text-red-500">Section not found</p>
                    </div>
                  );
                }

                return (
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none">
                      <h2 className="text-xl font-semibold mb-4 text-gray-900">
                        {selectedSection.title}
                      </h2>
                      <div className="mb-2 text-xs text-gray-500">
                        {activeTab === 'section-preview' ? 'Section Preview' : 'Preview'} • {(editedContent[sectionIdToPreview] || selectedSection.content)?.length || 0} characters
                      </div>
                      <PerfectMarkdownRenderer
                        key={`preview-${selectedSection.id}-${previewUpdateTrigger}-${activeTab}`}
                        content={editedContent[sectionIdToPreview] || selectedSection.content || ""}
                      />
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>Select a section to preview</p>
                    </div>
                  </div>
                );
              }
            })()
          }

          documentPreviewContent={
            <div className="p-6">
              <div className="prose prose-sm max-w-none">
                <h1 className="text-2xl font-bold mb-6 text-gray-900">Template Preview</h1>
                {sections.map((section) => (
                  <div key={section.id} className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900">
                      {section.title}
                    </h2>
                    <PerfectMarkdownRenderer
                      content={editedContent[section.id] || section.content || ""}
                    />
                  </div>
                ))}
                {sections.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No sections available</p>
                  </div>
                )}
              </div>
            </div>
          }
          actionButtons={
            <EditorActionButtons
              leftActions={
                <>
                  {selectedSectionId && sections.find(s => s.id === selectedSectionId)?.edited && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓ Modified
                    </span>
                  )}
                </>
              }
              rightActions={
                <>
                  {hasUnsavedChanges && onSave && (
                    <Button
                      onClick={onSave}
                      disabled={isSaving}
                      size="sm"
                      className="gap-2"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save Template
                    </Button>
                  )}
                </>
              }
            />
          }
        />
      </div>
    </div>
  );
}